import { DocumentFormContentShell, FieldMapper, type DocumentFormFieldRenderArgs } from '@cs-cms/ui';
import { useCurrentUserContext, useDocumentFormContext, useGetCurrentUser } from '@cs-cms/state-management';
import React from 'react';
import { useRtMasterFieldBehavior } from '../lib/useRtMasterFieldBehavior';
import { rtMasterFieldInteractionCallbacks } from '../lib/rtMasterFieldInteractionCallbacks';
import CpsLoader from '@cs-cms/ui/common/CpsLoader';
import { RtEditableFieldWrapper } from './RtEditableFieldWrapper';
import { stripRedundantMasterMetadata } from '../model/stripRedundantMasterMetadata';

export const RtDocumentForm: React.FC = () => {
  const { editedDocument } = useDocumentFormContext();
  const { selectedIndex } = useCurrentUserContext();
  const { data: currentUser } = useGetCurrentUser();
  const fieldBehavior = useRtMasterFieldBehavior();

  if (!editedDocument) {
    return null;
  }

  if (selectedIndex?.id === undefined) {
    return <CpsLoader />;
  }

  return (
    <DocumentFormContentShell
      indexId={selectedIndex.id}
      fieldInteractionCallbacks={rtMasterFieldInteractionCallbacks}
      transformMetadataForSubmit={stripRedundantMasterMetadata}
      renderCustomContent={() => {
        if (!currentUser?.id) {
          return null;
        }

        // TODO: Re-enable CDB widget once the widget team develops baldoAppId optional parameter.
        // return (
        //   <CdbWidget
        //     dealerId={String(selectedIndex.id)}
        //     userId={String(currentUser.id)}
        //     language={selectedLocale}
        //   />
        // );
        return null;
      }}
      renderField={(args: DocumentFormFieldRenderArgs) => {
        const { ctField, contentTypeFields, fieldTranslation, choices, isLoading, isEnabled, formRenderProps } = args;

        if (fieldBehavior.shouldHideField?.(ctField)) {
          return null;
        }

        const canEditReadOnlyField = fieldBehavior.canEditReadOnlyField?.(ctField, contentTypeFields) ?? false;
        const isInEditMode = fieldBehavior.isFieldInEditMode?.(ctField.name) ?? false;

        return (
          <FieldMapper
            key={ctField.name}
            ctField={ctField}
            fieldTranslation={fieldTranslation}
            choices={choices}
            isLoading={isLoading}
            onChange={value => {
              if (canEditReadOnlyField && isInEditMode) {
                formRenderProps.onChange(ctField.name, { value });
                return;
              }

              void args.onChangeDefault(value);
            }}
            onFilterChange={value => {
              void args.onFilterChangeDefault(value);
            }}
            onSearch={value => {
              void args.onSearchDefault(value);
            }}
            onRefresh={value => {
              void args.onRefreshDefault(value);
            }}
            isEnabled={isEnabled}
            getFieldDisabled={() =>
              (ctField.readOnly && !(canEditReadOnlyField && isInEditMode)) || isLoading || !isEnabled
            }
            renderFieldWrapper={wrapperProps => (
              <RtEditableFieldWrapper
                onChange={wrapperProps.onChange}
                onFocus={wrapperProps.onFocus}
                onBlur={wrapperProps.onBlur}
                name={wrapperProps.name}
                value={wrapperProps.value}
                validationMessage={wrapperProps.validationMessage}
                touched={wrapperProps.touched}
                modified={wrapperProps.modified}
                visited={wrapperProps.visited}
                id={wrapperProps.id}
                valid={wrapperProps.valid}
                disabled={wrapperProps.disabled}
                hint={wrapperProps.hint}
                label={wrapperProps.label}
                wrapperStyle={wrapperProps.wrapperStyle}
                optional={wrapperProps.optional}
                tooltipMessage={wrapperProps.tooltipMessage}
                isLoading={wrapperProps.isLoading}
                showRefreshButton={wrapperProps.showRefreshButton}
                onRefresh={wrapperProps.onRefresh}
                leadingActionButtons={wrapperProps.leadingActionButtons}
                canEditReadOnlyField={canEditReadOnlyField}
                isInEditMode={isInEditMode}
                onStartEdit={() =>
                  fieldBehavior.onStartEdit?.({
                    fieldName: ctField.name,
                    allFields: contentTypeFields,
                    formRenderProps
                  })
                }
                onSaveEdit={() => {
                  void fieldBehavior.onSaveEdit?.({
                    fieldName: ctField.name,
                    allFields: contentTypeFields,
                    formRenderProps,
                    runLookupForField: args.runLookupForField
                  });
                }}
                onCancelEdit={() =>
                  fieldBehavior.onCancelEdit?.({
                    fieldName: ctField.name,
                    allFields: contentTypeFields,
                    formRenderProps
                  })
                }
              >
                {wrapperProps.children}
              </RtEditableFieldWrapper>
            )}
          />
        );
      }}
    />
  );
};
